import { useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useCriticalAlertSound } from '@/hooks/useCriticalAlertSound';
import { useAuthStore } from '@/stores/authStore';
import {
  Bars3Icon,
  XMarkIcon,
  HomeIcon,
  MapIcon,
  TruckIcon,
  BuildingOffice2Icon,
  BellIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  UsersIcon,
  ArrowRightOnRectangleIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

const navigation = [
  { name: 'لوحة التحكم', href: '/', icon: HomeIcon },
  { name: 'الخريطة المباشرة', href: '/map', icon: MapIcon },
  { name: 'المركبات', href: '/vehicles', icon: TruckIcon },
  { name: 'الجهات', href: '/organizations', icon: BuildingOffice2Icon },
  { name: 'التنبيهات', href: '/alerts', icon: BellIcon },
  { name: 'التقارير', href: '/reports', icon: ChartBarIcon },
];

const adminNavigation = [
  { name: 'المستخدمون', href: '/users', icon: UsersIcon },
  { name: 'الإعدادات', href: '/settings', icon: Cog6ToothIcon },
];

export default function MainLayout() {
  // Audible alarm for tampering / crashes / critical alerts, mounted here so
  // it reaches the operator whatever page they are on.
  useCriticalAlertSound();

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdmin = user?.role === 'super_admin' || user?.role === 'ministry_admin';

  return (
    <div className="flex min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop — z-[1490] sits above page content but
          below the aside (z-[1500]). Slightly darker /60 for legibility. */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-[1490] bg-gray-900/60 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar
          Slide-in uses INLINE style for transform so we're immune to:
          - RTL flipping of Tailwind's `translate-x-full` in some browsers
            (that's why the panel was landing mid-screen on Android).
          - `lg:` variant resolution order glitches.
          On lg+ we override with `lg:!transform-none` so the panel sits
          in the flex flow rather than being translated.
          Width: w-[88vw] on phones (≈340px on a 390px screen) so the
          drawer reads as "covering the page" instead of looking like a
          mid-screen panel with content peeking on the side. */}
      <aside
        className={clsx(
          'fixed inset-y-0 right-0 z-[1500] w-[88vw] max-w-sm sm:max-w-md bg-white shadow-xl transition-transform duration-300',
          'lg:!transform-none lg:!w-72 xl:!w-80 lg:relative lg:shadow-none lg:border-l border-gray-200'
        )}
        style={{ transform: sidebarOpen ? 'translateX(0)' : 'translateX(100%)' }}
      >
        <div className="flex flex-col h-screen">
          {/* Logo */}
          <div className="flex items-center justify-between h-14 sm:h-16 3xl:h-20 4xl:h-24 5xl:h-32 px-4 sm:px-6 3xl:px-8 border-b border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2 sm:gap-3 3xl:gap-4">
              <div className="w-8 h-8 sm:w-10 sm:h-10 3xl:w-12 3xl:h-12 4xl:w-14 4xl:h-14 5xl:w-16 5xl:h-16 rounded-lg sm:rounded-xl 3xl:rounded-2xl gradient-primary flex items-center justify-center">
                <TruckIcon className="w-5 h-5 sm:w-6 sm:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10 text-white" />
              </div>
              <div>
                <h1 className="text-base sm:text-lg 3xl:text-xl 4xl:text-2xl 5xl:text-3xl font-bold text-gray-900">تتبع الإسعاف</h1>
                <p className="text-xs 3xl:text-sm 4xl:text-base 5xl:text-lg text-gray-500">النظام الوزاري</p>
              </div>
            </div>
            <button
              className="lg:hidden p-1.5 sm:p-2 rounded-lg hover:bg-gray-100"
              onClick={() => setSidebarOpen(false)}
            >
              <XMarkIcon className="w-5 h-5 sm:w-6 sm:h-6 3xl:w-7 3xl:h-7" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 sm:px-4 3xl:px-5 py-4 sm:py-6 3xl:py-8 space-y-1 3xl:space-y-2 overflow-y-auto">
            {navigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  clsx('sidebar-link text-sm sm:text-base 3xl:text-lg 4xl:text-xl 5xl:text-2xl', isActive && 'sidebar-link-active')
                }
                onClick={() => setSidebarOpen(false)}
              >
                <item.icon className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-7 4xl:h-7 5xl:w-8 5xl:h-8" />
                <span>{item.name}</span>
              </NavLink>
            ))}

            {isAdmin && (
              <>
                <div className="pt-4 mt-4 border-t border-gray-200">
                  <p className="px-4 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">
                    الإدارة
                  </p>
                  {adminNavigation.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      className={({ isActive }) =>
                        clsx('sidebar-link', isActive && 'sidebar-link-active')
                      }
                      onClick={() => setSidebarOpen(false)}
                    >
                      <item.icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </NavLink>
                  ))}
                </div>
              </>
            )}
          </nav>

          {/* User section */}
          <div className="p-3 sm:p-4 3xl:p-5 4xl:p-6 border-t border-gray-200 flex-shrink-0">
            <div className="flex items-center gap-2 sm:gap-3 3xl:gap-4 p-2 sm:p-3 3xl:p-4 rounded-lg 3xl:rounded-xl bg-gray-50">
              <div className="w-8 h-8 sm:w-10 sm:h-10 3xl:w-12 3xl:h-12 4xl:w-14 4xl:h-14 5xl:w-16 5xl:h-16 rounded-full bg-primary-100 flex items-center justify-center">
                <UserCircleIcon className="w-5 h-5 sm:w-6 sm:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm 3xl:text-base 4xl:text-lg 5xl:text-xl font-medium text-gray-900 truncate">
                  {user?.fullName}
                </p>
                <p className="text-xs 3xl:text-sm 4xl:text-base 5xl:text-lg text-gray-500 truncate">
                  {user?.email}
                </p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="w-full mt-2 sm:mt-3 3xl:mt-4 flex items-center justify-center gap-1 sm:gap-2 3xl:gap-3 px-3 sm:px-4 3xl:px-5 py-2 sm:py-2.5 3xl:py-3 4xl:py-4 rounded-lg 3xl:rounded-xl text-sm sm:text-base 3xl:text-lg 4xl:text-xl text-emergency-600 hover:bg-emergency-50 transition-colors"
            >
              <ArrowRightOnRectangleIcon className="w-4 h-4 sm:w-5 sm:h-5 3xl:w-6 3xl:h-6 4xl:w-7 4xl:h-7" />
              <span>تسجيل الخروج</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main content */}
      {/* min-w-0 is load-bearing: a flex item's automatic minimum size is its
          content's, so one wide table pushed this whole column past the
          viewport and gave every page a horizontal scrollbar. With it, wide
          content scrolls inside its own container instead. */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between h-14 sm:h-16 3xl:h-20 4xl:h-24 5xl:h-32 px-3 sm:px-4 lg:px-6 3xl:px-8 4xl:px-10">
            <button
              className="lg:hidden p-1.5 sm:p-2 3xl:p-3 rounded-lg hover:bg-gray-100"
              onClick={() => setSidebarOpen(true)}
            >
              <Bars3Icon className="w-5 h-5 sm:w-6 sm:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8" />
            </button>

            <div className="flex items-center gap-2 sm:gap-4 3xl:gap-6 mr-auto">
              {/* Notifications */}
              <button className="relative p-1.5 sm:p-2 3xl:p-3 rounded-lg hover:bg-gray-100">
                <BellIcon className="w-5 h-5 sm:w-6 sm:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8 5xl:w-10 5xl:h-10 text-gray-600" />
                <span className="absolute top-0.5 right-0.5 sm:top-1 sm:right-1 w-1.5 h-1.5 sm:w-2 sm:h-2 3xl:w-2.5 3xl:h-2.5 bg-emergency-500 rounded-full" />
              </button>

              {/* Connection status */}
              <div className="hidden sm:flex items-center gap-1 sm:gap-2 3xl:gap-3 px-2 sm:px-3 3xl:px-4 py-1 sm:py-1.5 3xl:py-2 rounded-full bg-success-50 text-success-700 text-xs sm:text-sm 3xl:text-base 4xl:text-lg">
                <span className="w-1.5 h-1.5 sm:w-2 sm:h-2 3xl:w-2.5 3xl:h-2.5 bg-success-500 rounded-full animate-pulse" />
                <span>متصل</span>
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-3 sm:p-4 lg:p-6 3xl:p-8 4xl:p-10 5xl:p-12">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
